import { ReactElement } from 'react'

import { useQuery } from '@tanstack/react-query'
import { getLanguageFromStorage } from 'helpers'

import featureFlags from 'const/featureFlags'
import { useCreditAccounts } from 'const/syfApiQueries'
import { CARECREDIT } from 'const/account/clients'
import DynamicYieldCard from './DynamicYieldCard'
import getDynamicYieldResponse from './getDynamicYieldResponse'
import { DynamicYieldWrapper } from './subcomponents'
import { DynamicYieldData } from './typings'

const allowedCareCreditAccounts = 1

const DynamicYieldGrid = (): ReactElement | null => {
  const lang = getLanguageFromStorage()

  const { data, isFetching, isError } = useQuery<DynamicYieldData>(
    ['dynamicYieldData', lang],
    async () => getDynamicYieldResponse()
  )

  const { data: { accounts = [] } = {}, isError: isCreditError } =
    useCreditAccounts()

  const careCreditAccountsList = accounts.filter(
    (acc) => acc?.product?.portfolio.toLowerCase() === CARECREDIT
  )
  // There has to be only one carecredit account in order to show DyBricks.
  const totalCareCreditAccounts = careCreditAccountsList.length

  const filteredCampaignsData = data?.campaigns?.filter((campaign) => {
    if (!campaign || !campaign.title) return false

    const isAccountRegistered =
      careCreditAccountsList[0]?.registration.registered
    // Exclude the card with `customCardArt` if there are more or less than one carecredit accounts or is not registered
    if (
      campaign.id === 'customCardArt' &&
      (totalCareCreditAccounts !== allowedCareCreditAccounts ||
        !isAccountRegistered)
    )
      return false

    // Exclude the card with id 'customCardArt' if the feature flag is disabled
    return !(
      campaign.id === 'customCardArt' && !featureFlags.isCustomCardArtEnabled
    )
  })

  const shouldRenderDY =
    !isFetching &&
    !isError &&
    !isCreditError &&
    Boolean(filteredCampaignsData?.length)

  if (shouldRenderDY) {
    return (
      <DynamicYieldWrapper data-test="dynamic-yield-card">
        {filteredCampaignsData.map((campaignData) => (
          <DynamicYieldCard
            key={campaignData.title}
            campaignData={campaignData}
          />
        ))}
      </DynamicYieldWrapper>
    )
  }

  return null
}

export default DynamicYieldGrid
